// ./src/contexts/CrewAccessContext/CrewAccessContext.tsx
//
// Resolves the caller's ACCESS to the event workspace they're currently in
// (docs/CREW-ACCESS.md). For a normal owner this reports isOwner=true and the app
// behaves exactly as before. For a CREW member (a cross-account account user, or a
// non-account password session), it reports their crew role + capabilities, which
// DashboardLayout uses to filter the event nav to the pages they may use and hide
// account-level chrome. The BE remains the authoritative gate — this only shapes
// the UI.
//
// One lightweight myEventAccess query per event navigation (memoized by eventId).
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import { useCurrentEvent } from "@/contexts/CurrentEventContext/CurrentEventContext";
import { useApp } from "@/contexts/AppContext/AppContext";
import { CrewAccessService, CrewSessionService, EventAccess } from "@/utils/api";
import { planFeatureMapOf } from "@/utils/planEntitlement";
import type { PlanFeatureMap } from "@/utils/planEntitlement";

export interface CrewAccessContextValue {
  /** The event the access below is for (matches the current workspace event). */
  eventId: string | null;
  /**
   * True while resolving access for the current event — including the first render after the event changes, before the
   * read has even started (#185 a review: it reported false then, so every `!crew.loading` gate judged a crew member by
   * their own account's role for one render, and a stage gate could leave a page they may open).
   */
  loading: boolean;
  /** True when the caller is an owner/admin of the event's account (normal full access). */
  isOwner: boolean;
  /**
   * True when the caller is working this event as CREW (not its owner) — the flag
   * that turns on crew-mode nav filtering. False for owners and outside an event.
   */
  isCrewMode: boolean;
  /** The caller's crew role for this event ('owner' for owners; a guest_role for crew; null = none). */
  role: string | null;
  /** The caller's capability strings on this event. */
  capabilities: Set<string>;
  /** Does the caller hold this capability on the current event? */
  hasCapability: (capability: string) => boolean;
  /**
   * #142 — the EVENT account's plan features, keyed by code (what the server's create gates read). null = unknown — the
   * plan gate then fails open, as the server does.
   */
  planFeatures: PlanFeatureMap | null;
  /**
   * ROADMAP #209 — the plan bought for THIS event, keyed by code like `planFeatures` (a feature either allows is allowed —
   * `useFeatureGate`'s `resolveEventGrant`); null = none bought, or unknown.
   */
  eventUpgrade: { planCode: string | null; planName: string | null; features: PlanFeatureMap } | null;
  /** Read the access again — after an upgrade is bought, so the event's new plan applies on this screen. */
  reload: () => Promise<void>;
  /**
   * #142 — the event belongs to the signed-in person's OWN account (the event's account id is theirs). Known as soon as
   * the event is, without waiting for — or depending on — the access read: an owner whose access read failed keeps
   * every "Add new" and their own plan.
   */
  ownsEvent: boolean;
}

const EMPTY: CrewAccessContextValue = {
  eventId: null,
  loading: false,
  isOwner: false,
  isCrewMode: false,
  role: null,
  capabilities: new Set(),
  hasCapability: () => false,
  planFeatures: null,
  eventUpgrade: null,
  reload: async () => undefined,
  ownsEvent: false,
};

const CrewAccessContext = createContext<CrewAccessContextValue>(EMPTY);

export const CrewAccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentEventId, currentEvent } = useCurrentEvent();
  const { isAuthenticated, user } = useApp();
  const ownAccountId = user?.account?.id ?? null;

  const [access, setAccess] = useState<EventAccess | null>(null);
  const [loading, setLoading] = useState(false);
  /** The event whose access read has SETTLED (answered or failed) — anything else is still being resolved. */
  const [resolvedFor, setResolvedFor] = useState<string | null>(null);
  const callRef = useRef(0);

  // Only worth resolving when there's an event AND the caller has SOME credential
  // (an account login or a crew session). Otherwise there's no access to report.
  const hasCredential = isAuthenticated || CrewSessionService.hasSession();

  /** Read this event's access (again). A newer read wins (`callRef`). */
  const readAccess = useCallback(async (): Promise<void> => {
    if (!currentEventId) {
      return;
    }

    const myCall = ++callRef.current;
    const readFor = currentEventId;
    setLoading(true);

    try {
      const result = await CrewAccessService.myEventAccess(currentEventId);

      if (myCall !== callRef.current) return;
      setLoading(false);
      setResolvedFor(readFor);
      const row =
        result.code === OP_RESULT_CODES.OK && Array.isArray(result.data)
          ? (result.data[0] as EventAccess | undefined)
          : undefined;
      setAccess(row?.eventId ? row : null);
    } catch {
      if (myCall !== callRef.current) return;
      setLoading(false);
      setResolvedFor(readFor);
      setAccess(null);
    }
  }, [currentEventId]);

  useEffect(() => {
    if (!currentEventId || !hasCredential) {
      setAccess(null);
      setLoading(false);
      return;
    }
    // Keep prior access if it's already for this event.
    if (access?.eventId === currentEventId) {
      return;
    }

    void readAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEventId, hasCredential]);

  const value = useMemo<CrewAccessContextValue>(() => {
    const forThisEvent = !!access && access.eventId === currentEventId;
    const capabilities = new Set<string>(forThisEvent ? access!.capabilities || [] : []);
    const isOwner = forThisEvent ? !!access!.isOwner : false;
    const role = forThisEvent ? access!.role : null;
    const isCrewMode = forThisEvent && !isOwner && !!role;
    const planRows = forThisEvent && Array.isArray(access!.planFeatures) ? access!.planFeatures : null;
    const planFeatures: PlanFeatureMap | null = planRows ? planFeatureMapOf(planRows) : null;
    const upgrade = forThisEvent ? access!.eventUpgrade ?? null : null;
    const eventUpgrade = upgrade
      ? { planCode: upgrade.planCode ?? null, planName: upgrade.planName ?? null, features: planFeatureMapOf(upgrade.features ?? []) }
      : null;

    // ⚠ Not settled for THIS event yet = loading, even before the effect has started the read.
    const settling = !!currentEventId && hasCredential && resolvedFor !== currentEventId;

    return {
      eventId: currentEventId,
      loading: loading || settling,
      isOwner,
      isCrewMode,
      role,
      capabilities,
      hasCapability: (capability: string) => capabilities.has(capability),
      planFeatures,
      eventUpgrade,
      reload: readAccess,
      ownsEvent:
        !!currentEventId && currentEvent?.id === currentEventId && !!currentEvent?.accountId && currentEvent.accountId === ownAccountId,
    };
  }, [access, currentEventId, loading, resolvedFor, hasCredential, currentEvent, ownAccountId, readAccess]);

  return <CrewAccessContext.Provider value={value}>{children}</CrewAccessContext.Provider>;
};

export const useCrewAccess = (): CrewAccessContextValue => useContext(CrewAccessContext);
